import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOMeta from '../../components/SEOMeta';
import ThemeToggle from '../../components/ThemeToggle';
import { useDarkMode } from '../../hooks/useDarkMode';
import { WHATSAPP_CTA_URL } from '../../data/whatsapp';

const N8N_AFILIADO_URL = 'https://n8n.goautomat.com/webhook/cadastrar-afiliado';

const PIX_TIPOS = [
  { value: 'CPF', label: 'CPF' },
  { value: 'EMAIL', label: 'E-mail' },
  { value: 'TELEFONE', label: 'Celular' },
  { value: 'CHAVE_ALEATORIA', label: 'Chave Aleatória' },
];

const FAQ = [
  {
    q: 'Tem custo ou mensalidade pra ser afiliado?',
    a: 'Zero. O cadastro é gratuito e você não paga nada para participar do programa, nem agora nem depois.',
  },
  {
    q: 'Como recebo as minhas comissões?',
    a: 'Em PIX, na chave que você cadastra. O repasse é feito sempre no dia 10 do mês seguinte à compra, somando todas as vendas confirmadas.',
  },
  {
    q: 'Tem limite de indicações por mês?',
    a: 'Não. Você pode indicar quantas pessoas quiser — e ganha 30% sobre o valor de cada venda confirmada com seu cupom, sem teto. Se o cliente comprar mais de uma música no mesmo pedido, sua comissão também aumenta.',
  },
  {
    q: 'Como acompanho minhas vendas e meus pagamentos?',
    a: 'No Portal do Afiliado, com acesso por e-mail e cupom. Você vê cada venda em tempo real, valor da comissão, status do pagamento e histórico completo.',
  },
  {
    q: 'O que faço se o cliente pedir reembolso?',
    a: 'Pedidos cancelados ou reembolsados não geram comissão. Mas a Inspira oferece refação gratuita em até 7 dias, então a maioria dos clientes prefere ajustar a música a pedir reembolso.',
  },
];

const TICKER_ITEMS = [
  '🎵 Comissão em PIX no dia 10 do mês seguinte',
  '💸 30% sobre o valor pago em cada venda',
  '🚀 Sem mensalidade, sem limite',
  '📲 Acompanhe vendas em tempo real no portal',
  '🎁 Kit de divulgação pronto pra postar',
  '🇧🇷 Música personalizada brasileira em até 30 minutos',
];

type FormState = {
  nome: string;
  email: string;
  whatsapp: string;
  cpf: string;
  pixTipo: string;
  pixChave: string;
  couponPrefix: string;
};

type UIState = {
  loading: boolean;
  error: string;
  success: boolean;
  successCupom: string;
};

// ─── Inline SVG: Vinyl record com R$10 no centro ─────────────────────────────
function VinylDisc({ isDark }: { isDark: boolean }) {
  const groove = isDark ? 'rgba(255,176,122,0.12)' : 'rgba(180,100,40,0.10)';
  const grooveStrong = isDark ? 'rgba(255,176,122,0.25)' : 'rgba(180,100,40,0.20)';
  const discBase = isDark ? '#181024' : '#1a1208';
  const labelOuter = '#ff914d';
  const labelInner = '#cc6620';
  return (
    <svg viewBox="0 0 400 400" style={{ width: '100%', height: '100%', display: 'block' }}>
      <defs>
        <radialGradient id="vinylDisc" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={discBase} />
          <stop offset="55%" stopColor={discBase} />
          <stop offset="100%" stopColor={isDark ? '#0a0610' : '#0c0805'} />
        </radialGradient>
        <radialGradient id="vinylShine" cx="38%" cy="32%" r="55%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.18)" />
          <stop offset="60%" stopColor="rgba(255,255,255,0)" />
        </radialGradient>
        <linearGradient id="vinylLabel" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={labelOuter} />
          <stop offset="100%" stopColor={labelInner} />
        </linearGradient>
      </defs>

      {/* Disc */}
      <circle cx="200" cy="200" r="195" fill="url(#vinylDisc)" />

      {/* Grooves */}
      {[185, 175, 165, 155, 145, 135, 125, 115].map((r, i) => (
        <circle
          key={r}
          cx="200"
          cy="200"
          r={r}
          fill="none"
          stroke={i % 2 === 0 ? grooveStrong : groove}
          strokeWidth={i % 3 === 0 ? 0.7 : 0.4}
        />
      ))}

      {/* Shine overlay */}
      <circle cx="200" cy="200" r="195" fill="url(#vinylShine)" />

      {/* Center label */}
      <circle cx="200" cy="200" r="78" fill="url(#vinylLabel)" />
      <circle cx="200" cy="200" r="78" fill="none" stroke="rgba(255,255,255,0.20)" strokeWidth="1.5" />

      {/* Center hole */}
      <circle cx="200" cy="200" r="6" fill={discBase} />
    </svg>
  );
}

// ─── Decorative groove line divider ───────────────────────────────────────────
function GrooveLine({ color = 'currentColor' }: { color?: string }) {
  return (
    <svg viewBox="0 0 1200 24" preserveAspectRatio="none" style={{ width: '100%', height: 24, display: 'block', opacity: 0.45 }}>
      <path
        d="M0 12 Q 30 4 60 12 T 120 12 T 180 12 T 240 12 T 300 12 T 360 12 T 420 12 T 480 12 T 540 12 T 600 12 T 660 12 T 720 12 T 780 12 T 840 12 T 900 12 T 960 12 T 1020 12 T 1080 12 T 1140 12 T 1200 12"
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

// ─── Hero stat pill ───────────────────────────────────────────────────────────
function StatPill({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: '12px 18px',
        borderRadius: 16,
        background: 'rgba(255,145,77,0.08)',
        border: '1px solid rgba(255,145,77,0.22)',
        minWidth: 120,
      }}
    >
      <span
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 800,
          fontSize: 20,
          letterSpacing: '-0.02em',
          color: 'var(--amber-deep)',
          marginTop: 2,
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Earnings calculator ─────────────────────────────────────────────────────
const QUICK_PICKS = [
  { label: 'Iniciante', sales: 1, sub: '1 venda/dia' },
  { label: 'Em ritmo', sales: 5, sub: '5 vendas/dia' },
  { label: 'No flow', sales: 20, sub: '20 vendas/dia' },
];

const COMMISSION_PER_SALE = 13.47;

function fmtBRL(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}
function fmtBRL2(v: number) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function EarningsCalc() {
  const [sales, setSales] = useState(5); // vendas POR DIA
  const [bumpKey, setBumpKey] = useState(0);
  const daily = sales * COMMISSION_PER_SALE;
  const monthly = daily * 30;
  const yearly = monthly * 12;
  const monthlyInt = Math.floor(monthly);
  const monthlyCents = Math.round((monthly - monthlyInt) * 100).toString().padStart(2, '0');
  const fillPct = Math.min(100, (sales / 100) * 100);

  function setSalesAnimated(v: number) {
    setSales(v);
    setBumpKey((k) => k + 1);
  }

  return (
    <section style={{ padding: '80px 16px', position: 'relative' }}>
      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <span
            style={{
              display: 'inline-block',
              padding: '5px 14px',
              borderRadius: 999,
              background: 'rgba(255,145,77,0.10)',
              border: '1px solid rgba(255,145,77,0.28)',
              color: 'var(--amber-deep)',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              fontFamily: "'DM Sans', sans-serif",
              marginBottom: 18,
            }}
          >
            Calculadora de ganhos
          </span>
          <h2
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 800,
              fontSize: 'clamp(32px, 5vw, 52px)',
              letterSpacing: '-0.035em',
              color: 'var(--heading-ink)',
              margin: 0,
              lineHeight: 1.05,
            }}
          >
            Quanto você pode ganhar?
          </h2>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              color: 'var(--text-muted)',
              maxWidth: 520,
              margin: '14px auto 0',
              lineHeight: 1.55,
            }}
          >
            Arraste para simular quantas vendas por dia você faz e veja o tamanho do PIX no dia 10 do mês seguinte.
          </p>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 12.5,
              color: 'var(--amber-deep)',
              fontWeight: 600,
              maxWidth: 520,
              margin: '8px auto 0',
              letterSpacing: '0.02em',
            }}
          >
            30% sobre o valor pago pelo cliente · projeção considerando 1 música por pedido
          </p>
        </div>

        <div
          className="glass"
          style={{
            borderRadius: 28,
            padding: 'clamp(28px, 5vw, 48px)',
            border: '1px solid var(--border-soft)',
          }}
        >
          {/* Big number */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
                margin: 0,
              }}
            >
              Comissão por mês
            </p>
            <div
              key={bumpKey}
              className="afil-bump"
              style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, marginTop: 10 }}
            >
              <span
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 'clamp(28px, 4vw, 40px)',
                  fontWeight: 700,
                  color: 'var(--text-muted)',
                  letterSpacing: '-0.02em',
                }}
              >
                R$
              </span>
              <span
                className="afil-number-grad"
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 'clamp(76px, 14vw, 144px)',
                  fontWeight: 800,
                  letterSpacing: '-0.06em',
                  lineHeight: 0.9,
                }}
              >
                {monthlyInt.toLocaleString('pt-BR')}
              </span>
              <span
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 'clamp(20px, 3vw, 32px)',
                  fontWeight: 700,
                  color: 'var(--text-muted)',
                  letterSpacing: '-0.02em',
                }}
              >
                ,{monthlyCents}
              </span>
            </div>
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 14,
                color: 'var(--text-muted)',
                margin: '6px 0 0',
              }}
            >
              {sales} {sales === 1 ? 'venda por dia' : 'vendas por dia'} · {fmtBRL2(daily)} por dia
            </p>
          </div>

          {/* Slider */}
          <div style={{ padding: '8px 4px 4px' }}>
            <input
              type="range"
              min={1}
              max={100}
              value={sales}
              onChange={(e) => setSalesAnimated(Number(e.target.value))}
              className="afil-slider"
              style={{ ['--fill' as string]: `${fillPct}%` } as React.CSSProperties}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: 10,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                color: 'var(--text-soft)',
              }}
            >
              <span>1</span>
              <span>25</span>
              <span>50</span>
              <span>75</span>
              <span>100</span>
            </div>
          </div>

          {/* Quick picks */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 28 }}>
            {QUICK_PICKS.map((p) => {
              const active = sales === p.sales;
              return (
                <button
                  key={p.sales}
                  type="button"
                  onClick={() => setSalesAnimated(p.sales)}
                  style={{
                    border: 'none',
                    borderRadius: 14,
                    padding: '12px 18px',
                    cursor: 'pointer',
                    fontFamily: "'DM Sans', sans-serif",
                    background: active ? 'var(--gold)' : 'rgba(255,145,77,0.08)',
                    color: active ? '#fff' : 'var(--amber-deep)',
                    boxShadow: active ? '0 6px 24px rgba(255,145,77,0.35)' : 'none',
                    transition: 'background 0.2s, box-shadow 0.2s, transform 0.1s',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    minWidth: 144,
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.background = 'rgba(255,145,77,0.16)';
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.background = 'rgba(255,145,77,0.08)';
                  }}
                >
                  <span style={{ fontWeight: 800, fontSize: 14, letterSpacing: '0.02em' }}>
                    {p.label} · {p.sales}
                  </span>
                  <span style={{ fontWeight: 500, fontSize: 11, opacity: 0.85, marginTop: 1 }}>{p.sub}</span>
                </button>
              );
            })}
          </div>

          {/* Year projection */}
          <div
            style={{
              marginTop: 32,
              padding: '18px 22px',
              borderRadius: 16,
              background: 'rgba(255,145,77,0.10)',
              border: '1px dashed rgba(255,145,77,0.36)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: '0.10em',
                  textTransform: 'uppercase',
                  color: 'var(--amber-deep)',
                  margin: 0,
                }}
              >
                Em 12 meses
              </p>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 'clamp(22px, 3vw, 28px)',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: 'var(--heading-ink)',
                  margin: '4px 0 0',
                }}
              >
                {fmtBRL(yearly)}
              </p>
            </div>
            <a
              href="#cadastro"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '12px 22px',
                borderRadius: 999,
                background: 'var(--gold)',
                color: '#fff',
                fontWeight: 700,
                fontSize: 14,
                fontFamily: "'DM Sans', sans-serif",
                textDecoration: 'none',
                boxShadow: '0 6px 22px rgba(255,145,77,0.40)',
                whiteSpace: 'nowrap',
              }}
            >
              Quero esse ganho →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── How it works ─────────────────────────────────────────────────────────────
const STEPS = [
  {
    n: '01',
    title: 'Escolha o nome do seu cupom',
    desc: 'Use seu nome, apelido ou marca. Adicionamos +10 ao final automaticamente. Pronto em segundos.',
  },
  {
    n: '02',
    title: 'Compartilhe nas suas redes',
    desc: 'Use o kit pronto: links rastreáveis para WhatsApp e site, copies prontas para Stories e bio.',
  },
  {
    n: '03',
    title: 'Receba comissão em PIX',
    desc: 'Cada música vendida com seu cupom rende 30% do valor pago pelo cliente. O total do mês cai na sua chave PIX no dia 10 do mês seguinte.',
  },
];

function HowItWorks() {
  return (
    <section style={{ padding: '80px 16px', position: 'relative' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <h2
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 800,
              fontSize: 'clamp(32px, 5vw, 52px)',
              letterSpacing: '-0.035em',
              color: 'var(--heading-ink)',
              margin: 0,
              lineHeight: 1.05,
            }}
          >
            Como funciona
          </h2>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              color: 'var(--text-muted)',
              maxWidth: 520,
              margin: '14px auto 0',
              lineHeight: 1.55,
            }}
          >
            Três passos. Sem cadastro pago, sem burocracia.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18 }}>
          {STEPS.map((s) => (
            <div
              key={s.n}
              className="glass"
              style={{
                borderRadius: 22,
                padding: '32px 26px',
                border: '1px solid var(--border-soft)',
                display: 'flex',
                flexDirection: 'column',
                gap: 14,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  top: 14,
                  right: 14,
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 800,
                  fontSize: 76,
                  letterSpacing: '-0.05em',
                  color: 'rgba(255,145,77,0.10)',
                  lineHeight: 1,
                  pointerEvents: 'none',
                }}
              >
                {s.n}
              </span>
              <span
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 800,
                  fontSize: 13,
                  letterSpacing: '0.10em',
                  color: 'var(--gold)',
                  textTransform: 'uppercase',
                }}
              >
                Passo {s.n}
              </span>
              <h3
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 800,
                  fontSize: 22,
                  letterSpacing: '-0.025em',
                  color: 'var(--heading-ink)',
                  margin: 0,
                  lineHeight: 1.15,
                }}
              >
                {s.title}
              </h3>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 15,
                  color: 'var(--text-muted)',
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                {s.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Kit preview ──────────────────────────────────────────────────────────────
const KIT_FEATURES = [
  {
    icon: '🔗',
    title: 'Links rastreáveis prontos',
    desc: 'Um link de WhatsApp e um de site, ambos já com seu cupom embutido. Só copiar e postar.',
  },
  {
    icon: '✍️',
    title: 'Copies pra postar agora',
    desc: 'Textos prontos pra Stories, bio e WhatsApp. Versões longa, curta e Instagram — escolhe e cola.',
  },
  {
    icon: '📊',
    title: 'Painel de vendas em tempo real',
    desc: 'Cada compra com seu cupom aparece automaticamente, com valor da comissão e status do pagamento.',
  },
  {
    icon: '💸',
    title: 'PIX no dia 10 do mês seguinte',
    desc: 'Tudo que você faturou no mês cai junto na sua chave PIX, automaticamente — sem precisar pedir.',
  },
];

function KitPreview() {
  return (
    <section style={{ padding: '80px 16px', position: 'relative' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 36 }}>
          <div style={{ maxWidth: 560 }}>
            <span
              style={{
                display: 'inline-block',
                padding: '5px 14px',
                borderRadius: 999,
                background: 'rgba(196,168,255,0.18)',
                border: '1px solid rgba(196,168,255,0.40)',
                color: 'var(--amber-deep)',
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.10em',
                textTransform: 'uppercase',
                fontFamily: "'DM Sans', sans-serif",
                marginBottom: 14,
              }}
            >
              O que você recebe
            </span>
            <h2
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 800,
                fontSize: 'clamp(30px, 4.6vw, 46px)',
                letterSpacing: '-0.03em',
                color: 'var(--heading-ink)',
                margin: 0,
                lineHeight: 1.08,
              }}
            >
              Tudo pronto pra você começar a vender.
            </h2>
          </div>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 15,
              color: 'var(--text-muted)',
              maxWidth: 360,
              lineHeight: 1.6,
              margin: 0,
            }}
          >
            No portal você encontra os materiais e o painel de vendas. Sem precisar criar nada do zero.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {KIT_FEATURES.map((f) => (
            <div
              key={f.title}
              className="glass"
              style={{
                borderRadius: 20,
                padding: '24px 22px',
                border: '1px solid var(--border-soft)',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: 'rgba(255,145,77,0.10)',
                  border: '1px solid rgba(255,145,77,0.22)',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 22,
                }}
              >
                {f.icon}
              </span>
              <h3
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 800,
                  fontSize: 17,
                  letterSpacing: '-0.02em',
                  color: 'var(--heading-ink)',
                  margin: 0,
                  lineHeight: 1.25,
                }}
              >
                {f.title}
              </h3>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  lineHeight: 1.55,
                  margin: 0,
                }}
              >
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Form helpers ─────────────────────────────────────────────────────────────
function inputBaseStyle(): React.CSSProperties {
  return {
    width: '100%',
    padding: '14px 16px',
    borderRadius: 14,
    border: '1.5px solid var(--border-soft)',
    background: 'var(--cream)',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 15,
    color: 'var(--text-body)',
    outline: 'none',
    transition: 'border-color 0.18s, box-shadow 0.18s',
    boxSizing: 'border-box',
  };
}

function inputFocus(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) {
  e.currentTarget.style.borderColor = 'var(--gold)';
  e.currentTarget.style.boxShadow = '0 0 0 4px rgba(255,145,77,0.12)';
}
function inputBlur(e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) {
  e.currentTarget.style.borderColor = 'var(--border-soft)';
  e.currentTarget.style.boxShadow = 'none';
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label
      style={{
        display: 'block',
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 12,
        fontWeight: 700,
        color: 'var(--text-strong)',
        marginBottom: 7,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      }}
    >
      {children}
      {required && <span style={{ color: '#D64C2B', marginLeft: 4 }}>*</span>}
    </label>
  );
}

// ─── Success screen ───────────────────────────────────────────────────────────
function SuccessScreen({ cupom }: { cupom: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '8px 4px 4px' }}>
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 84,
          height: 84,
          borderRadius: '50%',
          background: 'rgba(50,160,95,0.16)',
          marginBottom: 20,
          fontSize: 40,
        }}
      >
        ✅
      </div>
      <h2
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontWeight: 800,
          fontSize: 32,
          letterSpacing: '-0.03em',
          color: 'var(--heading-ink)',
          margin: 0,
        }}
      >
        Você está dentro!
      </h2>
      <p
        style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 15,
          color: 'var(--text-muted)',
          margin: '10px auto 28px',
          maxWidth: 460,
          lineHeight: 1.55,
        }}
      >
        Seu cupom foi criado e já tá no ar. A próxima venda com ele te paga em PIX.
      </p>

      <div
        style={{
          display: 'inline-block',
          padding: '24px 36px',
          borderRadius: 24,
          background: 'rgba(255,145,77,0.10)',
          border: '2px dashed var(--gold)',
          marginBottom: 28,
        }}
      >
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            margin: 0,
          }}
        >
          Seu cupom
        </p>
        <p
          className="afil-number-grad"
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 44,
            fontWeight: 800,
            letterSpacing: '0.05em',
            margin: '6px 0 4px',
            lineHeight: 1,
          }}
        >
          {cupom}
        </p>
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 13,
            color: 'var(--text-muted)',
            margin: 0,
          }}
        >
          10% de desconto pra quem usar · 30% de comissão pra você
        </p>
      </div>

      <div
        style={{
          textAlign: 'left',
          maxWidth: 480,
          margin: '0 auto 28px',
          padding: '22px 24px',
          borderRadius: 18,
          background: 'rgba(255,255,255,0.55)',
          border: '1px solid var(--border-soft)',
        }}
      >
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            color: 'var(--gold)',
            margin: 0,
          }}
        >
          Próximos passos
        </p>
        <ol style={{ margin: '14px 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[
            <>Entre no <strong>Portal do Afiliado</strong> e copie seus links e textos prontos.</>,
            <>Compartilhe seu cupom <strong>{cupom}</strong> nas suas redes.</>,
            <>Acompanhe vendas em tempo real e receba PIX automático.</>,
          ].map((item, i) => (
            <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: 'var(--gold)',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 13,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {i + 1}
              </span>
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14.5, color: 'var(--text-body)', lineHeight: 1.55 }}>
                {item}
              </span>
            </li>
          ))}
        </ol>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Link
          to="/afiliados/portal"
          style={{
            padding: '14px 28px',
            borderRadius: 999,
            background: 'var(--gold)',
            color: '#fff',
            fontWeight: 700,
            fontSize: 15,
            fontFamily: "'DM Sans', sans-serif",
            textDecoration: 'none',
            boxShadow: '0 6px 22px rgba(255,145,77,0.40)',
          }}
        >
          Entrar no portal →
        </Link>
        <Link
          to="/"
          style={{
            padding: '14px 24px',
            borderRadius: 999,
            border: '1.5px solid var(--border-soft)',
            color: 'var(--text-strong)',
            fontWeight: 600,
            fontSize: 15,
            fontFamily: "'DM Sans', sans-serif",
            textDecoration: 'none',
          }}
        >
          Voltar ao site
        </Link>
      </div>
    </div>
  );
}

// ─── FAQ section ──────────────────────────────────────────────────────────────
function FaqSection() {
  return (
    <section style={{ padding: '80px 16px', position: 'relative' }}>
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <span
            style={{
              display: 'inline-block',
              padding: '5px 14px',
              borderRadius: 999,
              background: 'rgba(255,145,77,0.10)',
              border: '1px solid rgba(255,145,77,0.28)',
              color: 'var(--amber-deep)',
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              fontFamily: "'DM Sans', sans-serif",
              marginBottom: 14,
            }}
          >
            Perguntas frequentes
          </span>
          <h2
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 800,
              fontSize: 'clamp(30px, 4.6vw, 44px)',
              letterSpacing: '-0.03em',
              color: 'var(--heading-ink)',
              margin: 0,
              lineHeight: 1.1,
            }}
          >
            Tirando suas dúvidas.
          </h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {FAQ.map((item, i) => (
            <details
              key={i}
              className="glass afil-faq"
              style={{
                borderRadius: 16,
                border: '1px solid var(--border-soft)',
                padding: '4px 8px',
              }}
            >
              <summary
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 14,
                  padding: '18px 14px',
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 700,
                  fontSize: 16,
                  color: 'var(--heading-ink)',
                  lineHeight: 1.4,
                  letterSpacing: '-0.01em',
                }}
              >
                <span>{item.q}</span>
                <span
                  className="afil-faq-icon"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: 'rgba(255,145,77,0.14)',
                    color: 'var(--amber-deep)',
                    fontSize: 18,
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'transform 0.2s ease',
                  }}
                >
                  +
                </span>
              </summary>
              <p
                style={{
                  padding: '0 14px 18px',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14.5,
                  color: 'var(--text-muted)',
                  lineHeight: 1.7,
                  margin: 0,
                }}
              >
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function AfiliadosPage() {
  const isDark = useDarkMode();
  const [form, setForm] = useState<FormState>({
    nome: '',
    email: '',
    whatsapp: '',
    cpf: '',
    pixTipo: '',
    pixChave: '',
    couponPrefix: '',
  });
  const [ui, setUi] = useState<UIState>({
    loading: false,
    error: '',
    success: false,
    successCupom: '',
  });

  const formRef = useRef<HTMLDivElement | null>(null);

  const couponPreview = useMemo(
    () => form.couponPrefix.toUpperCase().replace(/[^A-Z]/g, '') + '10',
    [form.couponPrefix]
  );
  const hasCouponPreview = couponPreview.length > 2;

  function formatPhone(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits.length ? `(${digits}` : '';
    if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  function formatCPF(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  }

  function handleChange(field: keyof FormState, value: string) {
    let formatted = value;
    if (field === 'whatsapp') formatted = formatPhone(value);
    else if (field === 'cpf') formatted = formatCPF(value);
    setForm((f) => ({ ...f, [field]: formatted }));
    if (ui.error) setUi((u) => ({ ...u, error: '' }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const prefixClean = form.couponPrefix.toUpperCase().replace(/[^A-Z]/g, '');
    const cpfDigits = form.cpf.replace(/\D/g, '');
    if (!form.nome.trim()) return setUi((u) => ({ ...u, error: 'Nome é obrigatório.' }));
    if (!form.email.trim()) return setUi((u) => ({ ...u, error: 'E-mail é obrigatório.' }));
    if (cpfDigits.length !== 11) return setUi((u) => ({ ...u, error: 'CPF é obrigatório (11 dígitos).' }));
    if (!form.pixTipo) return setUi((u) => ({ ...u, error: 'Escolha o tipo de chave PIX.' }));
    if (!form.pixChave.trim()) return setUi((u) => ({ ...u, error: 'Chave PIX é obrigatória.' }));
    if (prefixClean.length < 2) return setUi((u) => ({ ...u, error: 'O nome do cupom precisa de pelo menos 2 letras.' }));

    setUi((u) => ({ ...u, loading: true, error: '' }));
    try {
      const res = await fetch(N8N_AFILIADO_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: form.nome.trim(),
          email: form.email.trim(),
          whatsapp: form.whatsapp.trim(),
          cpf: cpfDigits,
          pix_tipo: form.pixTipo,
          pix_chave: form.pixChave.trim(),
          coupon_prefix: prefixClean,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setUi({ loading: false, error: '', success: true, successCupom: data.cupom });
      } else {
        setUi((u) => ({ ...u, loading: false, error: data.erro || 'Erro ao cadastrar. Tente novamente.' }));
      }
    } catch {
      setUi((u) => ({ ...u, loading: false, error: 'Erro de conexão. Verifique sua internet e tente novamente.' }));
    }
  }

  // Scroll to form on success to show it nicely
  useEffect(() => {
    if (ui.success && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [ui.success]);

  return (
    <>
      <SEOMeta
        title="Programa de Afiliados | Inspira Músicas"
        description="Indique músicas personalizadas e receba 30% sobre cada venda em PIX no dia 10 do mês seguinte. Cadastre-se grátis no programa de afiliados oficial da Inspira Músicas."
        path="/afiliados"
      />

      <div className="afiliados-shell" style={{ minHeight: '100vh', fontFamily: "'DM Sans', sans-serif" }}>
        {/* HEADER */}
        <header
          className="glass"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 50,
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,170,110,0.20)'}`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            background: isDark ? 'rgba(10,5,20,0.65)' : 'rgba(255,250,243,0.78)',
          }}
        >
          <div className="max-w-6xl mx-auto px-4 md:px-8" style={{ height: 78, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center' }}>
              <img src={isDark ? '/logo-dark.png' : '/logo-light.png'} alt="Inspira Músicas" style={{ height: 52, objectFit: 'contain' }} />
            </Link>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <ThemeToggle />
              <Link
                to="/afiliados/portal"
                style={{
                  border: '1.5px solid var(--border-soft)',
                  color: 'var(--text-strong)',
                  borderRadius: 999,
                  padding: '8px 16px',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                  transition: 'border-color 0.2s, color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--gold)';
                  e.currentTarget.style.color = 'var(--gold)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-soft)';
                  e.currentTarget.style.color = 'var(--text-strong)';
                }}
              >
                Já sou afiliado
              </Link>
              <a
                href={WHATSAPP_CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'var(--gold)',
                  color: '#fff',
                  borderRadius: 999,
                  padding: '8px 18px',
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                  boxShadow: '0 4px 18px rgba(255,145,77,0.32)',
                }}
              >
                Criar música
              </a>
            </div>
          </div>
        </header>

        {/* HERO */}
        <section style={{ padding: 'clamp(48px, 7vw, 100px) 16px 64px', position: 'relative' }}>
          <div className="max-w-6xl mx-auto" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)', gap: 48, alignItems: 'center' }}>
            {/* Left: editorial headline */}
            <div>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 14px',
                  borderRadius: 999,
                  background: 'rgba(255,145,77,0.10)',
                  border: '1px solid rgba(255,145,77,0.28)',
                  color: 'var(--amber-deep)',
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  fontFamily: "'DM Sans', sans-serif",
                  marginBottom: 22,
                }}
              >
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--gold)', boxShadow: '0 0 0 4px rgba(255,145,77,0.20)' }} />
                Programa de afiliados oficial
              </span>

              <h1
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 800,
                  fontSize: 'clamp(48px, 8vw, 96px)',
                  letterSpacing: '-0.045em',
                  color: 'var(--heading-ink)',
                  margin: 0,
                  lineHeight: 0.95,
                }}
              >
                <span style={{ display: 'block', fontStyle: 'italic', fontWeight: 500, fontSize: 'clamp(28px, 4.5vw, 54px)', color: 'var(--text-muted)', letterSpacing: '-0.02em', marginBottom: 6 }}>
                  Você indica.
                </span>
                <span style={{ display: 'block', fontSize: 'clamp(40px, 6.5vw, 78px)', letterSpacing: '-0.04em' }}>
                  Vira música.
                </span>
                <span className="afil-number-grad" style={{ display: 'block', fontSize: 'clamp(56px, 11vw, 130px)', letterSpacing: '-0.055em', lineHeight: 0.95 }}>
                  Vira PIX.
                </span>
              </h1>

              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 'clamp(15px, 1.6vw, 18px)',
                  color: 'var(--text-muted)',
                  lineHeight: 1.6,
                  marginTop: 24,
                  maxWidth: 520,
                }}
              >
                <strong style={{ color: 'var(--text-strong)' }}>30% de comissão</strong> em cada música vendida com seu cupom. Sem mensalidade. Sem limite. PIX automático no dia 10 do mês seguinte à venda.
              </p>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 28 }}>
                <a
                  href="#cadastro"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '16px 28px',
                    borderRadius: 999,
                    background: 'var(--gold)',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 16,
                    fontFamily: "'DM Sans', sans-serif",
                    textDecoration: 'none',
                    boxShadow: '0 8px 28px rgba(255,145,77,0.42)',
                    transition: 'background 0.2s, transform 0.12s, box-shadow 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--gold-hover)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--gold)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Começar grátis em 2 minutos →
                </a>
                <a
                  href="#calculadora"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '16px 24px',
                    borderRadius: 999,
                    border: '1.5px solid var(--border-soft)',
                    color: 'var(--text-strong)',
                    fontWeight: 600,
                    fontSize: 15,
                    fontFamily: "'DM Sans', sans-serif",
                    textDecoration: 'none',
                    transition: 'border-color 0.2s, color 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.color = 'var(--gold)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border-soft)';
                    e.currentTarget.style.color = 'var(--text-strong)';
                  }}
                >
                  Quanto eu posso ganhar?
                </a>
              </div>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 28 }}>
                <StatPill label="Comissão" value="30%" />
                <StatPill label="Pagamento" value="Dia 10 do mês+1" />
                <StatPill label="Limite" value="Sem teto" />
              </div>
            </div>

            {/* Right: vinyl visual */}
            <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
              <div
                style={{
                  position: 'relative',
                  width: 'clamp(280px, 38vw, 420px)',
                  height: 'clamp(280px, 38vw, 420px)',
                  filter: isDark ? 'drop-shadow(0 30px 60px rgba(0,0,0,0.55))' : 'drop-shadow(0 24px 50px rgba(180,90,30,0.28))',
                }}
              >
                <div className="afil-vinyl-spin" style={{ width: '100%', height: '100%' }}>
                  <VinylDisc isDark={isDark} />
                </div>
                {/* R$10 label que NÃO gira (sobreposto ao centro) */}
                <div
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pointerEvents: 'none',
                  }}
                >
                  <div
                    style={{
                      textAlign: 'center',
                      color: '#fff',
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  >
                    <p
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        margin: 0,
                        opacity: 0.85,
                      }}
                    >
                      Comissão
                    </p>
                    <p
                      style={{
                        fontSize: 'clamp(48px, 7.5vw, 80px)',
                        fontWeight: 800,
                        letterSpacing: '-0.04em',
                        lineHeight: 1,
                        margin: '4px 0 2px',
                      }}
                    >
                      30%
                    </p>
                    <p
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.10em',
                        textTransform: 'uppercase',
                        opacity: 0.9,
                        margin: 0,
                      }}
                    >
                      por venda
                    </p>
                  </div>
                </div>

                {/* Floating badges */}
                <div
                  style={{
                    position: 'absolute',
                    top: -8,
                    left: -8,
                    background: 'var(--surface-white)',
                    padding: '8px 14px',
                    borderRadius: 999,
                    border: '1px solid var(--border-soft)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.10)',
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--heading-ink)',
                    transform: 'rotate(-6deg)',
                  }}
                >
                  💸 PIX dia 10 do mês+1
                </div>
                <div
                  style={{
                    position: 'absolute',
                    bottom: 24,
                    right: -16,
                    background: 'var(--surface-white)',
                    padding: '8px 14px',
                    borderRadius: 999,
                    border: '1px solid var(--border-soft)',
                    boxShadow: '0 6px 20px rgba(0,0,0,0.10)',
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13,
                    fontWeight: 700,
                    color: 'var(--heading-ink)',
                    transform: 'rotate(5deg)',
                  }}
                >
                  ♾️ Sem limite
                </div>
              </div>
            </div>
          </div>

          {/* Mobile fallback: stack the visual below */}
          <style>{`
            @media (max-width: 880px) {
              .afiliados-shell main h1 { line-height: 1.0; }
            }
            @media (max-width: 720px) {
              .afiliados-shell .max-w-6xl > div[style*='grid-template-columns: minmax(0, 1.15fr)'],
              .afiliados-shell section > div[style*='grid-template-columns: minmax(0, 1.15fr)'] {
                grid-template-columns: 1fr !important;
              }
            }
          `}</style>
        </section>

        {/* TICKER STRIP */}
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            padding: '14px 0',
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,170,110,0.18)'}`,
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,170,110,0.18)'}`,
            background: isDark ? 'rgba(0,0,0,0.20)' : 'rgba(255,145,77,0.05)',
          }}
        >
          <div className="afil-marquee-track" style={{ display: 'flex', gap: 40, whiteSpace: 'nowrap', width: 'max-content' }}>
            {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
              <span
                key={i}
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--text-strong)',
                  letterSpacing: '-0.005em',
                }}
              >
                {item}
              </span>
            ))}
          </div>
        </div>

        <div style={{ color: 'var(--gold)', maxWidth: 1100, margin: '40px auto 0', padding: '0 16px' }}>
          <GrooveLine />
        </div>

        {/* CALCULATOR */}
        <div id="calculadora">
          <EarningsCalc />
        </div>

        <div style={{ color: 'var(--gold)', maxWidth: 1100, margin: '0 auto', padding: '0 16px' }}>
          <GrooveLine />
        </div>

        {/* HOW IT WORKS */}
        <HowItWorks />

        {/* KIT PREVIEW */}
        <KitPreview />

        {/* SIGNUP FORM */}
        <section id="cadastro" ref={formRef} style={{ padding: '40px 16px 80px', position: 'relative' }}>
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <div
              className="glass"
              style={{
                borderRadius: 28,
                padding: 'clamp(32px, 5vw, 56px)',
                border: '1px solid var(--border-soft)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {ui.success ? (
                <SuccessScreen cupom={ui.successCupom} />
              ) : (
                <>
                  <div style={{ textAlign: 'center', marginBottom: 28 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '5px 14px',
                        borderRadius: 999,
                        background: 'rgba(255,145,77,0.12)',
                        border: '1px solid rgba(255,145,77,0.30)',
                        color: 'var(--amber-deep)',
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: '0.10em',
                        textTransform: 'uppercase',
                        fontFamily: "'DM Sans', sans-serif",
                        marginBottom: 14,
                      }}
                    >
                      Cadastro grátis · 2 minutos
                    </span>
                    <h2
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 800,
                        fontSize: 'clamp(28px, 4vw, 40px)',
                        letterSpacing: '-0.03em',
                        color: 'var(--heading-ink)',
                        margin: 0,
                        lineHeight: 1.1,
                      }}
                    >
                      Crie seu cupom agora.
                    </h2>
                    <p
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 15,
                        color: 'var(--text-muted)',
                        margin: '10px 0 0',
                        lineHeight: 1.55,
                      }}
                    >
                      Você só precisa do seu nome, e-mail e chave PIX. O cupom fica pronto em segundos.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    <div>
                      <FieldLabel required>Seu nome completo</FieldLabel>
                      <input
                        type="text"
                        placeholder="Ex: Maria Silva"
                        value={form.nome}
                        onChange={(e) => handleChange('nome', e.target.value)}
                        style={inputBaseStyle()}
                        onFocus={inputFocus}
                        onBlur={inputBlur}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                      <div>
                        <FieldLabel required>E-mail</FieldLabel>
                        <input
                          type="email"
                          placeholder="seu@email.com"
                          value={form.email}
                          onChange={(e) => handleChange('email', e.target.value)}
                          style={inputBaseStyle()}
                          onFocus={inputFocus}
                          onBlur={inputBlur}
                        />
                      </div>
                      <div>
                        <FieldLabel>WhatsApp</FieldLabel>
                        <input
                          type="tel"
                          placeholder="(11) 99999-9999"
                          value={form.whatsapp}
                          onChange={(e) => handleChange('whatsapp', e.target.value)}
                          style={inputBaseStyle()}
                          onFocus={inputFocus}
                          onBlur={inputBlur}
                        />
                      </div>
                    </div>

                    <div>
                      <FieldLabel required>CPF</FieldLabel>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="000.000.000-00"
                        value={form.cpf}
                        maxLength={14}
                        onChange={(e) => handleChange('cpf', e.target.value)}
                        style={inputBaseStyle()}
                        onFocus={inputFocus}
                        onBlur={inputBlur}
                      />
                      <p
                        style={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: 12.5,
                          color: 'var(--text-muted)',
                          marginTop: 8,
                          marginBottom: 0,
                        }}
                      >
                        Necessário pra emissão dos pagamentos. Não compartilhamos com ninguém.
                      </p>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        margin: '6px 0',
                      }}
                    >
                      <span
                        style={{
                          flex: 1,
                          height: 1,
                          background: 'linear-gradient(90deg, transparent 0%, var(--border-soft) 50%, transparent 100%)',
                        }}
                      />
                      <span
                        style={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: 11,
                          fontWeight: 800,
                          letterSpacing: '0.10em',
                          textTransform: 'uppercase',
                          color: 'var(--gold)',
                        }}
                      >
                        Onde você recebe
                      </span>
                      <span
                        style={{
                          flex: 1,
                          height: 1,
                          background: 'linear-gradient(90deg, transparent 0%, var(--border-soft) 50%, transparent 100%)',
                        }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.5fr)', gap: 16 }}>
                      <div>
                        <FieldLabel required>Tipo</FieldLabel>
                        <select
                          value={form.pixTipo}
                          onChange={(e) => handleChange('pixTipo', e.target.value)}
                          style={{ ...inputBaseStyle(), appearance: 'none', cursor: 'pointer' }}
                          onFocus={inputFocus}
                          onBlur={inputBlur}
                        >
                          <option value="">Selecione...</option>
                          {PIX_TIPOS.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <FieldLabel required>Chave PIX</FieldLabel>
                        <input
                          type="text"
                          placeholder={
                            form.pixTipo === 'CPF'
                              ? '000.000.000-00'
                              : form.pixTipo === 'EMAIL'
                                ? 'seu@email.com'
                                : form.pixTipo === 'TELEFONE'
                                  ? '(11) 99999-9999'
                                  : form.pixTipo === 'CHAVE_ALEATORIA'
                                    ? 'Cole sua chave aleatória'
                                    : 'Selecione o tipo primeiro'
                          }
                          value={form.pixChave}
                          onChange={(e) => handleChange('pixChave', e.target.value)}
                          disabled={!form.pixTipo}
                          style={{ ...inputBaseStyle(), opacity: form.pixTipo ? 1 : 0.6 }}
                          onFocus={inputFocus}
                          onBlur={inputBlur}
                        />
                      </div>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        margin: '6px 0',
                      }}
                    >
                      <span
                        style={{
                          flex: 1,
                          height: 1,
                          background: 'linear-gradient(90deg, transparent 0%, var(--border-soft) 50%, transparent 100%)',
                        }}
                      />
                      <span
                        style={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: 11,
                          fontWeight: 800,
                          letterSpacing: '0.10em',
                          textTransform: 'uppercase',
                          color: 'var(--gold)',
                        }}
                      >
                        Seu cupom
                      </span>
                      <span
                        style={{
                          flex: 1,
                          height: 1,
                          background: 'linear-gradient(90deg, transparent 0%, var(--border-soft) 50%, transparent 100%)',
                        }}
                      />
                    </div>

                    <div>
                      <FieldLabel required>Nome do seu cupom</FieldLabel>
                      <div style={{ display: 'flex', alignItems: 'stretch', gap: 10 }}>
                        <input
                          type="text"
                          placeholder="Ex: MARIA, JOAO, SILVA"
                          value={form.couponPrefix}
                          maxLength={15}
                          onChange={(e) => handleChange('couponPrefix', e.target.value.replace(/[^a-zA-Z]/g, ''))}
                          style={{ ...inputBaseStyle(), textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1 }}
                          onFocus={inputFocus}
                          onBlur={inputBlur}
                        />
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '0 16px',
                            borderRadius: 14,
                            background: 'rgba(255,145,77,0.10)',
                            border: '1.5px solid rgba(255,145,77,0.28)',
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 18,
                            fontWeight: 800,
                            color: 'var(--amber-deep)',
                            letterSpacing: '0.04em',
                          }}
                        >
                          + 10
                        </span>
                      </div>
                      <p
                        style={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: 12.5,
                          color: 'var(--text-muted)',
                          marginTop: 8,
                          marginBottom: 0,
                        }}
                      >
                        Apenas letras. O número 10 é adicionado automaticamente.
                      </p>
                    </div>

                    {hasCouponPreview && (
                      <div
                        style={{
                          padding: '16px 18px',
                          borderRadius: 16,
                          background: 'rgba(255,145,77,0.10)',
                          border: '1.5px dashed rgba(255,145,77,0.42)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 14,
                        }}
                      >
                        <span style={{ fontSize: 26 }}>🎟️</span>
                        <div>
                          <p
                            style={{
                              fontFamily: "'DM Sans', sans-serif",
                              fontSize: 11,
                              fontWeight: 800,
                              letterSpacing: '0.10em',
                              textTransform: 'uppercase',
                              color: 'var(--text-muted)',
                              margin: 0,
                            }}
                          >
                            Seu cupom ficará assim
                          </p>
                          <p
                            className="afil-number-grad"
                            style={{
                              fontFamily: "'DM Sans', sans-serif",
                              fontSize: 28,
                              fontWeight: 800,
                              letterSpacing: '0.04em',
                              margin: '2px 0 0',
                              lineHeight: 1.1,
                            }}
                          >
                            {couponPreview}
                          </p>
                        </div>
                      </div>
                    )}

                    {ui.error && (
                      <div
                        style={{
                          padding: '12px 16px',
                          borderRadius: 12,
                          background: 'rgba(192,56,43,0.10)',
                          border: '1px solid rgba(192,56,43,0.32)',
                          color: '#a53125',
                          fontSize: 14,
                          fontFamily: "'DM Sans', sans-serif",
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span>⚠️</span>
                        <span>{ui.error}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={ui.loading}
                      style={{
                        width: '100%',
                        padding: '17px 24px',
                        borderRadius: 999,
                        border: 'none',
                        background: ui.loading ? 'var(--earth-light)' : 'var(--gold)',
                        color: '#fff',
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 16,
                        fontWeight: 700,
                        cursor: ui.loading ? 'not-allowed' : 'pointer',
                        boxShadow: ui.loading ? 'none' : '0 8px 28px rgba(255,145,77,0.42)',
                        transition: 'background 0.2s, box-shadow 0.2s, transform 0.12s',
                        marginTop: 6,
                      }}
                      onMouseEnter={(e) => {
                        if (!ui.loading) {
                          e.currentTarget.style.background = 'var(--gold-hover)';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!ui.loading) {
                          e.currentTarget.style.background = 'var(--gold)';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      {ui.loading ? 'Criando seu cupom...' : 'Criar meu cupom grátis →'}
                    </button>

                    <p
                      style={{
                        textAlign: 'center',
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: 12,
                        color: 'var(--text-muted)',
                        margin: 0,
                      }}
                    >
                      Ao se cadastrar, você concorda com os{' '}
                      <Link to="/termos-de-servico" style={{ color: 'var(--gold)', textDecoration: 'underline' }}>
                        Termos
                      </Link>
                      {' '}e a{' '}
                      <Link to="/politica-de-privacidade" style={{ color: 'var(--gold)', textDecoration: 'underline' }}>
                        Política de Privacidade
                      </Link>
                      .
                    </p>
                  </form>
                </>
              )}
            </div>

            <p
              style={{
                textAlign: 'center',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 14,
                color: 'var(--text-muted)',
                marginTop: 20,
              }}
            >
              Já tem cadastro?{' '}
              <Link
                to="/afiliados/portal"
                style={{ color: 'var(--gold)', fontWeight: 700, textDecoration: 'none' }}
              >
                Entrar no Portal do Afiliado →
              </Link>
            </p>
          </div>
        </section>

        {/* FAQ */}
        <FaqSection />

        {/* FINAL CTA BAND */}
        <section style={{ padding: '60px 16px 100px' }}>
          <div className="max-w-5xl mx-auto">
            <div
              style={{
                position: 'relative',
                borderRadius: 32,
                padding: 'clamp(36px, 6vw, 64px)',
                background:
                  'linear-gradient(135deg, var(--gold) 0%, var(--amber-deep) 100%)',
                color: '#fff',
                overflow: 'hidden',
                boxShadow: '0 24px 60px rgba(255,145,77,0.35)',
              }}
            >
              {/* Decorative grooves */}
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: -120,
                  right: -120,
                  width: 320,
                  height: 320,
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.20)',
                  pointerEvents: 'none',
                }}
              />
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: -80,
                  right: -80,
                  width: 240,
                  height: 240,
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.18)',
                  pointerEvents: 'none',
                }}
              />
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  top: -40,
                  right: -40,
                  width: 160,
                  height: 160,
                  borderRadius: '50%',
                  border: '1px solid rgba(255,255,255,0.16)',
                  pointerEvents: 'none',
                }}
              />

              <div style={{ position: 'relative', zIndex: 1, maxWidth: 640 }}>
                <p
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 12,
                    fontWeight: 800,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    margin: 0,
                    opacity: 0.85,
                  }}
                >
                  Bora começar?
                </p>
                <h2
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 800,
                    fontSize: 'clamp(30px, 5vw, 52px)',
                    letterSpacing: '-0.035em',
                    margin: '10px 0 16px',
                    lineHeight: 1.05,
                  }}
                >
                  Seu próximo PIX te espera.
                </h2>
                <p
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 16,
                    lineHeight: 1.6,
                    margin: 0,
                    opacity: 0.92,
                  }}
                >
                  Cadastro em 2 minutos, cupom pronto na hora. Sem custo, sem mensalidade, sem letra miúda.
                </p>
                <a
                  href="#cadastro"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 28,
                    padding: '16px 28px',
                    borderRadius: 999,
                    background: '#fff',
                    color: 'var(--amber-deep)',
                    fontWeight: 800,
                    fontSize: 16,
                    fontFamily: "'DM Sans', sans-serif",
                    textDecoration: 'none',
                    boxShadow: '0 8px 28px rgba(0,0,0,0.18)',
                    transition: 'transform 0.12s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  Quero meu cupom →
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer
          style={{
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(255,170,110,0.20)'}`,
            padding: '32px 16px',
          }}
        >
          <div className="max-w-5xl mx-auto" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <img src={isDark ? '/logo-dark.png' : '/logo-light.png'} alt="Inspira Músicas" style={{ height: 48, objectFit: 'contain' }} />
            <p
              style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 12,
                color: 'var(--text-muted)',
                textAlign: 'center',
                margin: 0,
              }}
            >
              © {new Date().getFullYear()} Inspira Músicas · AUTOMAT TECNOLOGIA DA INFORMACAO LTDA
            </p>
            <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Link to="/termos-de-servico" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: 'var(--text-muted)', textDecoration: 'none' }}>
                Termos
              </Link>
              <Link to="/politica-de-privacidade" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: 'var(--text-muted)', textDecoration: 'none' }}>
                Privacidade
              </Link>
              <Link to="/afiliados/portal" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: 'var(--gold)', fontWeight: 700, textDecoration: 'none' }}>
                Portal do Afiliado
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
